import {
  ConvertedQuantityData,
  TaskQuantityDTO,
  TaskTypesToKorean,
  taskTypes,
} from "../../services/task.interface";
import { ColumnsType } from "antd/es/table";
import { TableColumn } from "../../../../components/table/TableColumn";
import styled from "styled-components";
import { TableBase } from "../../../../components/table/TableBase";
import { useEffect, useState } from "react";
import { EditableCell } from "../EditableCell";
import {
  calculateObjectTotalSum,
  convertQuantityData,
  convertInputDataToTaskQuantityData,
  initializeTaskQuantityDTO,
} from "../../utils/task.utils";
import { Button, Modal, Table } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";
import { putTaskQuantity } from "../../services/task.service";
import { useAuthContext } from "../../../auth/context/useAuthContext";
import {
  vehicleTypeLabel,
  vehicleTypes,
} from "../../../scooters/service/scooter.interface";

interface TaskQuantityCountTableProps {
  dataSource: TaskQuantityDTO;
  fetch: () => void;
  loading: boolean;
}

type TaskQuantityTarget = "capacity" | "results";

const taskQuantityTargetLabels: Record<TaskQuantityTarget, string> = {
  capacity: "배정 작업 수",
  results: "작업 완료 수",
};

const taskQuantityTargets: readonly TaskQuantityTarget[] = [
  "results",
  "capacity",
];
const taskQuantityTargetsForDeltaValidation: readonly TaskQuantityTarget[] = [
  "results",
];
const taskTypesForDeltaValidation: readonly (typeof taskTypes)[number][] =
  taskTypes.filter((taskType) => taskType !== "footpadClean");

interface TaskQuantityDeltaDetail {
  target: TaskQuantityTarget;
  vehicleType: (typeof vehicleTypes)[number];
  taskType: (typeof taskTypes)[number];
  originalValue: number;
  editedValue: number;
  delta: number;
}

interface TaskQuantityDeltaGroup {
  target: TaskQuantityTarget;
  vehicleType: (typeof vehicleTypes)[number];
  totalDelta: number;
  details: TaskQuantityDeltaDetail[];
}

function getTaskQuantityValue(
  data: TaskQuantityDTO,
  target: TaskQuantityTarget,
  vehicleType: (typeof vehicleTypes)[number],
  taskType: (typeof taskTypes)[number]
): number {
  return data[target]?.[vehicleType]?.[taskType] ?? 0;
}

function getEditedTaskQuantityValue(
  originalData: TaskQuantityDTO,
  editedData: TaskQuantityDTO,
  target: TaskQuantityTarget,
  vehicleType: (typeof vehicleTypes)[number],
  taskType: (typeof taskTypes)[number]
): number {
  const originalValue = getTaskQuantityValue(
    originalData,
    target,
    vehicleType,
    taskType
  );

  return editedData[target]?.[vehicleType]?.[taskType] ?? originalValue;
}

function calculateTaskQuantityDeltaByVehicleType(
  originalData: TaskQuantityDTO,
  editedData: TaskQuantityDTO,
  target: TaskQuantityTarget,
  vehicleType: (typeof vehicleTypes)[number],
  targetTaskTypes: readonly (typeof taskTypes)[number][] = taskTypes
): number {
  return targetTaskTypes.reduce(
    (taskTotal, taskType) =>
      taskTotal +
      getEditedTaskQuantityValue(
        originalData,
        editedData,
        target,
        vehicleType,
        taskType
      ) -
      getTaskQuantityValue(originalData, target, vehicleType, taskType),
    0
  );
}

function formatDelta(value: number): string {
  return value > 0 ? `+${value}` : value.toString();
}

function getTaskQuantityDeltaDetails(
  originalData: TaskQuantityDTO,
  editedData: TaskQuantityDTO,
  target: TaskQuantityTarget,
  vehicleType: (typeof vehicleTypes)[number],
  targetTaskTypes: readonly (typeof taskTypes)[number][] = taskTypes
): TaskQuantityDeltaDetail[] {
  return targetTaskTypes.reduce<TaskQuantityDeltaDetail[]>(
    (details, taskType) => {
      const originalValue = getTaskQuantityValue(
        originalData,
        target,
        vehicleType,
        taskType
      );
      const editedValue = getEditedTaskQuantityValue(
        originalData,
        editedData,
        target,
        vehicleType,
        taskType
      );
      const delta = editedValue - originalValue;

      if (delta !== 0) {
        details.push({
          target,
          vehicleType,
          taskType,
          originalValue,
          editedValue,
          delta,
        });
      }

      return details;
    },
    []
  );
}

function getTaskQuantityDeltaGroups(
  originalData: TaskQuantityDTO,
  editedData: TaskQuantityDTO,
  targetTaskTypes: readonly (typeof taskTypes)[number][] = taskTypes,
  targets: readonly TaskQuantityTarget[] = taskQuantityTargets
): TaskQuantityDeltaGroup[] {
  return targets.flatMap((target) =>
    vehicleTypes
      .map((vehicleType) => ({
        target,
        vehicleType,
        totalDelta: calculateTaskQuantityDeltaByVehicleType(
          originalData,
          editedData,
          target,
          vehicleType,
          targetTaskTypes
        ),
        details: getTaskQuantityDeltaDetails(
          originalData,
          editedData,
          target,
          vehicleType,
          targetTaskTypes
        ),
      }))
      .filter(({ details }) => details.length > 0)
  );
}

function getUnbalancedTaskQuantityDetails(
  originalData: TaskQuantityDTO,
  editedData: TaskQuantityDTO
): TaskQuantityDeltaGroup[] {
  return getTaskQuantityDeltaGroups(
    originalData,
    editedData,
    taskTypesForDeltaValidation,
    taskQuantityTargetsForDeltaValidation
  ).filter(({ totalDelta }) => totalDelta > 0);
}

function TaskQuantityDeltaGroupList({
  groups,
}: {
  groups: TaskQuantityDeltaGroup[];
}) {
  return (
    <>
      {groups.map((item) => (
        <ValidationSection key={`${item.target}-${item.vehicleType}`}>
          <p className="title">
            {taskQuantityTargetLabels[item.target]} /{" "}
            {vehicleTypeLabel[item.vehicleType]} 증감 합계:{" "}
            {formatDelta(item.totalDelta)}건
          </p>
          <ul>
            {item.details.map((detail) => (
              <li
                key={`${detail.target}-${detail.vehicleType}-${detail.taskType}`}
              >
                {vehicleTypeLabel[detail.vehicleType]} /{" "}
                {TaskTypesToKorean[detail.taskType]}: 기존{" "}
                {detail.originalValue}건, 수정 {detail.editedValue}건 (
                {formatDelta(detail.delta)}건)
              </li>
            ))}
          </ul>
        </ValidationSection>
      ))}
    </>
  );
}

export function TaskQuantityCountTable(props: TaskQuantityCountTableProps) {
  const { dataSource, fetch, loading } = props;
  const [quantityCountData, setQuantityCountData] = useState<TaskQuantityDTO>(
    initializeTaskQuantityDTO()
  );
  const [edit, setEdit] = useState<boolean>(false);
  const { auth, hasPermission } = useAuthContext();

  useEffect(() => {
    setQuantityCountData(dataSource);
  }, [dataSource]);

  useEffect(() => {
    if (!edit) setQuantityCountData(dataSource);
  }, [edit]);

  const columns: ColumnsType<ConvertedQuantityData> = [
    TableColumn("작업항목", (row) => vehicleTypeLabel[row.vehicleType], {
      align: "center",
      fixed: true,
      width: 88,
    }),
    TableColumn(
      "교체/재배치",
      (row) => (
        <EditableCell
          type={dataSource.collector.type}
          capacity={row.replaceReallocate.capacity}
          edit={edit}
          results={row.replaceReallocate.results}
          targetQuantity="replaceReallocate"
          targetVehicleType={row.vehicleType}
          quantityCountData={quantityCountData}
          setQuantityCountData={setQuantityCountData}
        />
      ),
      {
        width: 160,
      }
    ),
    TableColumn(
      "교체",
      (row) => (
        <EditableCell
          type={dataSource.collector.type}
          capacity={row.replace.capacity}
          edit={edit}
          results={row.replace.results}
          targetQuantity="replace"
          targetVehicleType={row.vehicleType}
          quantityCountData={quantityCountData}
          setQuantityCountData={setQuantityCountData}
        />
      ),
      {
        width: 160,
      }
    ),
    TableColumn(
      "재배치",
      (row) => (
        <EditableCell
          type={dataSource.collector.type}
          capacity={row.reallocate.capacity}
          edit={edit}
          results={row.reallocate.results}
          targetQuantity="reallocate"
          targetVehicleType={row.vehicleType}
          quantityCountData={quantityCountData}
          setQuantityCountData={setQuantityCountData}
        />
      ),
      {
        width: 160,
      }
    ),
    TableColumn(
      "수거",
      (row) => (
        <EditableCell
          type={dataSource.collector.type}
          capacity={row.capture.capacity}
          edit={edit}
          results={row.capture.results}
          targetQuantity="capture"
          targetVehicleType={row.vehicleType}
          quantityCountData={quantityCountData}
          setQuantityCountData={setQuantityCountData}
        />
      ),
      {
        width: 160,
      }
    ),
    TableColumn(
      "배치",
      (row) => (
        <EditableCell
          type={dataSource.collector.type}
          capacity={row.allocate.capacity}
          edit={edit}
          results={row.allocate.results}
          targetQuantity="allocate"
          targetVehicleType={row.vehicleType}
          quantityCountData={quantityCountData}
          setQuantityCountData={setQuantityCountData}
        />
      ),
      {
        width: 160,
      }
    ),
    TableColumn(
      "닦기",
      (row) => (
        <EditableCell
          type={dataSource.collector.type}
          capacity={row.footpadClean.capacity}
          edit={edit}
          results={row.footpadClean.results}
          targetQuantity="footpadClean"
          targetVehicleType={row.vehicleType}
          quantityCountData={quantityCountData}
          setQuantityCountData={setQuantityCountData}
        />
      ),
      {
        width: 160,
      }
    ),
  ];

  if (!auth) return <></>;

  return (
    <TableContainer>
      <StyledTable
        bordered={false}
        rowKey={(r) => r.vehicleType}
        dataSource={convertQuantityData({
          capacity: dataSource.capacity,
          results: dataSource.results,
        })}
        columns={columns}
        loading={loading}
        pagination={false}
        summary={() => {
          const result = convertInputDataToTaskQuantityData(
            quantityCountData.results
          );
          return (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={1}>
                  합계 (건)
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} colSpan={1}>
                  {`총 ${result.replaceReallocate}건`}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} colSpan={1}>
                  {`총 ${result.replace}건`}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} colSpan={1}>
                  {`총 ${result.reallocate}건`}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} colSpan={1}>
                  {`총 ${result.capture}건`}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} colSpan={1}>
                  {`총 ${result.allocate}건`}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} colSpan={1}>
                  {`총 ${result.footpadClean}건`}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={1}>
                  합계
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} colSpan={6}>
                  {`총 ${calculateObjectTotalSum(result)}건`}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />
      {auth && hasPermission("TaskQuantityEditEnable") &&
        (edit ? (
          <ButtonsContainer>
            <Button
              onClick={async () => {
                const unbalancedDetails = getUnbalancedTaskQuantityDetails(
                  dataSource,
                  quantityCountData
                );
                if (unbalancedDetails.length > 0) {
                  Modal.error({
                    title: "작업 수량 수정값을 확인해주세요.",
                    width: 720,
                    content: (
                      <ValidationDetails>
                        <p>
                          완료 수량은 닦기를 제외한 원본 대비 +/- 합계가 0
                          이하이어야 저장할 수 있습니다.
                        </p>
                        <TaskQuantityDeltaGroupList
                          groups={unbalancedDetails}
                        />
                      </ValidationDetails>
                    ),
                  });
                  return;
                }

                const changedDetails = getTaskQuantityDeltaGroups(
                  dataSource,
                  quantityCountData
                );
                if (changedDetails.length === 0) {
                  Modal.info({
                    title: "수정된 작업 수량이 없습니다.",
                  });
                  return;
                }

                Modal.confirm({
                  title: "작업 수량 수정사항을 저장할까요?",
                  width: 720,
                  okText: "저장",
                  cancelText: "취소",
                  content: (
                    <ValidationDetails>
                      <p>아래 수정사항을 확인해주세요.</p>
                      <TaskQuantityDeltaGroupList groups={changedDetails} />
                    </ValidationDetails>
                  ),
                  onOk: async () => {
                    await putTaskQuantity({
                      date: quantityCountData.date,
                      collectorId: quantityCountData.collector.id,
                      capacity: quantityCountData.capacity,
                      results: quantityCountData.results,
                    });
                    fetch();
                    setEdit(false);
                  },
                });
              }}
              shape="circle"
              icon={<CheckOutlined />}
            />
            <Button
              onClick={() => setEdit(false)}
              shape="circle"
              icon={<CloseOutlined />}
            />
          </ButtonsContainer>
        ) : (
          <ButtonsContainer>
            <Button
              onClick={() => setEdit(true)}
              shape="circle"
              icon={<EditOutlined />}
            />
          </ButtonsContainer>
        ))}
    </TableContainer>
  );
}

const TableContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: #edeff2;
`;

const StyledTable = styled<any>(TableBase)`
  width: 100%;
  .ant-table-cell-fix-left,
  .ant-table-cell-fix-left-last {
    background-color: #fafafa;
    padding-left: 8px !important;
  }
  th {
    text-align: center !important;
    background-color: #edeff2 !important;
  }
  .ant-table,
  .ant-table-small,
  .ant-table-bordered,
  .ant-table-scroll-horizontal {
    margin: 0 !important;
  }

  .ant-table-summary {
    border: 1px;
    background: #ffffff;
    font-weight: 400;
    text-align: center;
    & > tr > :first-child {
      text-align: center;
    }
    & > :last-child {
      & > * {
        font-weight: 700;
      }
    }
  }
  .ant-table-summary > tr > th,
  .ant-table-summary > tr > td {
    border-bottom: none;
    border-top: 1px solid #f0f0f0;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 0 16px;
  & > * {
    margin: 0 4px;
  }
`;

const ValidationDetails = styled.div`
  & > p {
    margin: 0 0 12px;
  }
`;

const ValidationSection = styled.div`
  margin-top: 12px;

  & > .title {
    font-weight: 700;
    margin: 0 0 6px;
  }

  & > ul {
    max-height: 180px;
    margin: 0;
    padding-left: 18px;
    overflow-y: auto;
  }

  & li {
    line-height: 22px;
  }
`;
